"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"

import { useLocale } from "@/lib/locale-context"
import { localeLabels, locales, type Locale } from "@/lib/content"
import { localePath, splitLocale } from "@/lib/seo-paths"
import { cn } from "@/lib/utils"

/** Pages that only exist in English; other languages fall back to their home. */
const englishOnly = new Set(["/cv"])

const languageNames: Record<Locale, string> = { en: "English", pt: "Português", de: "Deutsch" }

/**
 * The same page in another language, as real links: each language is its
 * own URL, so crawlers find and index all three.
 */
export function LanguageSwitcher() {
  const { locale } = useLocale()
  const { path } = splitLocale(usePathname() ?? "/")

  return (
    <div className="flex items-center gap-2" role="group" aria-label="Language">
      {locales.map((loc: Locale) => {
        const target = loc !== "en" && englishOnly.has(path) ? "/" : path
        return (
          <Link
            key={loc}
            href={localePath(loc, target)}
            hrefLang={loc}
            lang={loc}
            title={languageNames[loc]}
            className={cn(
              "font-display text-[13px] transition-colors",
              locale === loc ? "text-accent" : "text-muted-foreground hover:text-foreground"
            )}
            aria-current={locale === loc ? "page" : undefined}
          >
            {localeLabels[loc]}
          </Link>
        )
      })}
    </div>
  )
}
